import { SettingsValueProps } from './components/settings/type';

export const HOST_API = process.env.REACT_APP_API_URL || '';

const apiEntry = process.env.NODE_ENV === 'production' ? 'api' : 'api';
const loginServiceApiEntry = process.env.NODE_ENV === 'production' ? 'api' : 'api';

export const API = {
  login: `${loginServiceApiEntry}/login`,
  logout: `${loginServiceApiEntry}/logout`,
  refresh: `${loginServiceApiEntry}/refresh`,
  // user activity ping for the 30-minute idle timeout
  sessionActivity: `${loginServiceApiEntry}/session/activity`,

  // client security settings (password expiry + mandatory 2FA), user-management, KYP only
  securitySettingsClients: `${loginServiceApiEntry}/security-settings/clients`,
  securitySettingsClient: (clientId: string) =>
    `${loginServiceApiEntry}/security-settings/clients/${encodeURIComponent(clientId)}`,
  // accounts exempt from the idle timeout (e.g. test accounts)
  sessionExemptions: `${loginServiceApiEntry}/security-settings/session-exemptions`,
  sessionExemption: (userId: string) =>
    `${loginServiceApiEntry}/security-settings/session-exemptions/${encodeURIComponent(userId)}`,
  // access control: suspend a whole client / block one user (KYP only)
  clientSuspension: (clientId: string) =>
    `${loginServiceApiEntry}/security-settings/clients/${encodeURIComponent(clientId)}/suspension`,
  userBlock: (userId: string) =>
    `${loginServiceApiEntry}/security-settings/users/${encodeURIComponent(userId)}/block`,
  // multi-client access: which clients a user belongs to (KYP only)
  userClients: (userId: string) =>
    `${loginServiceApiEntry}/security-settings/users/${encodeURIComponent(userId)}/clients`,
  userClient: (userId: string, clientId: string) =>
    `${loginServiceApiEntry}/security-settings/users/${encodeURIComponent(
      userId
    )}/clients/${encodeURIComponent(clientId)}`,

  getClients: `${apiEntry}/admin/clients`,
  createClient: `${apiEntry}/admin/client`,

  // per-client product entitlements (module feature flags)
  getClientsFeatures: `${apiEntry}/admin/clients/features`,
  setClientFeatures: (clientId: string) => `${apiEntry}/admin/client/${clientId}/features`,
  // Supervisors management (shell-less model 2026-09-09: mapping keyed by
  // tenant client id; names resolved from client documents).
  getSupervisors: `${apiEntry}/admin/supervisors`,
  setSupervisorFlag: (clientId: string) => `${apiEntry}/admin/client/${clientId}/supervisor`,
  upsertSupervisorMapping: (supervisorId: string) =>
    `${apiEntry}/admin/supervisor/${supervisorId}/mapping`,
  deleteSupervisorMapping: (supervisorId: string, tenantOrPortfolioId: string) =>
    `${apiEntry}/admin/supervisor/${supervisorId}/mapping/${tenantOrPortfolioId}`,
  // Sender domains: each client's email domain with the mail provider (Brevo).
  getOnboardingDomains: `${apiEntry}/admin/onboarding/domains`,
  addOnboardingDomain: `${apiEntry}/admin/onboarding/domains`,
  deleteOnboardingDomain: (domain: string) => `${apiEntry}/admin/onboarding/domains/${domain}`,

  // per-client monthly minimum commitments (billing caps) + current usage
  documentScanningLibrary: `${apiEntry}/admin/document-scanning/library`,
  documentScanningProvider: `${apiEntry}/admin/document-scanning/provider`,
  documentScanningTypes: `${apiEntry}/admin/document-scanning/types`,
  documentScanningType: (id: string) =>
    `${apiEntry}/admin/document-scanning/types/${encodeURIComponent(id)}`,
  documentScanningChecks: `${apiEntry}/admin/document-scanning/checks`,
  documentScanningCheck: (id: string) =>
    `${apiEntry}/admin/document-scanning/checks/${encodeURIComponent(id)}`,
  documentScanningArchive: (id: string) =>
    `${apiEntry}/admin/document-scanning/checks/${encodeURIComponent(id)}/archive`,
  documentScanningRestore: (id: string) =>
    `${apiEntry}/admin/document-scanning/checks/${encodeURIComponent(id)}/restore`,

  getClientsCommitments: `${apiEntry}/admin/clients/commitments`,
  setClientCommitments: (clientId: string) => `${apiEntry}/admin/client/${clientId}/commitments`,
  inviteUser: `${apiEntry}/clients/invite`,

  getUsers: `${apiEntry}/admin/users`,
  deleteUser: (userId: string) => `${apiEntry}/admin/user/${encodeURIComponent(userId)}`,
  updateUser: (userId: string) => `${apiEntry}/admin/user/${encodeURIComponent(userId)}`,

  getMatches: `${apiEntry}/admin/individuals/company`,
  getCompanies: `${apiEntry}/admin/individuals/company/all`,
  updateMatches: `${apiEntry}/admin/individuals/reviews`,

  relatedInfoLogs: `${apiEntry}/admin/user/clients`,

  clientsStatistic: `${apiEntry}/admin/stats/users`,
  clientsCompanies: `${apiEntry}/admin/stats/companies`,

  allCompaniesList: `${apiEntry}/admin/companies/all`,
  reloadService: `${apiEntry}/admin/update`,

  downloadPdf: `${apiEntry}/admin/pdf/client`,

  // platform usage monitoring — backed by ingestion-service/usagemeter, which
  // records one event per client action. The catalogue endpoint drives the
  // page's filters and labels, so new metered actions appear without a
  // frontend release.
  usageCatalogue: `${apiEntry}/admin/usage/catalogue`,
  usageSummary: `${apiEntry}/admin/usage/summary`,
  usageTimeseries: `${apiEntry}/admin/usage/timeseries`,
  usageBreakdown: `${apiEntry}/admin/usage/breakdown`,
  usageClients: `${apiEntry}/admin/usage/clients`,
  usageEvents: `${apiEntry}/admin/usage/events`,
  usageExport: `${apiEntry}/admin/usage/export`,
  usageHealth: `${apiEntry}/admin/usage/health`,
  // The portfolio picker's vocabulary. Lists portfolios that exist now; a
  // deleted one still shows in a breakdown, labelled by ID.
  usagePortfolios: `${apiEntry}/admin/usage/portfolios`,
  // Transaction-monitoring volume, rolled up from the hourly metrics the
  // client's own widgets read. Not the ingest edge: see txmetrics package doc.
  usageTransactions: `${apiEntry}/admin/usage/transactions`,
  // Standing count of monitored domains per client (WebComply).
  usageDomains: `${apiEntry}/admin/usage/domains`,

  // Transactions API setup page — backed by server/txapiadmin on both Go
  // services: per-client setup status, HMAC credentials, screening policy,
  // registrations and the ingest / screening request log.
  txApiClients: `${apiEntry}/admin/transactions-api/clients`,
  txApiClient: (clientId: string) =>
    `${apiEntry}/admin/transactions-api/clients/${encodeURIComponent(clientId)}`,
  txApiKeys: (clientId: string) =>
    `${apiEntry}/admin/transactions-api/clients/${encodeURIComponent(clientId)}/keys`,
  txApiKey: (clientId: string, keyId: string) =>
    `${apiEntry}/admin/transactions-api/clients/${encodeURIComponent(clientId)}/keys/${encodeURIComponent(keyId)}`,
  txApiKeyRotate: (clientId: string, keyId: string) =>
    `${apiEntry}/admin/transactions-api/clients/${encodeURIComponent(clientId)}/keys/${encodeURIComponent(keyId)}/rotate`,
  txApiPolicy: (clientId: string) =>
    `${apiEntry}/admin/transactions-api/clients/${encodeURIComponent(clientId)}/screening-policy`,
  txApiRegistrations: (clientId: string) =>
    `${apiEntry}/admin/transactions-api/clients/${encodeURIComponent(clientId)}/registrations`,
  txApiLog: (clientId: string) =>
    `${apiEntry}/admin/transactions-api/clients/${encodeURIComponent(clientId)}/log`,
  txApiLogSummary: (clientId: string) =>
    `${apiEntry}/admin/transactions-api/clients/${encodeURIComponent(clientId)}/log/summary`,
  txApiSigningExample: `${apiEntry}/admin/transactions-api/signing-example`,

  // supplier-side cost: what we paid CreditSafe / Acuris, including the
  // automatic refreshes that are billed to no one
  supplierPulls: `${apiEntry}/admin/usage/supplier-pulls`,
};

// LAYOUT
// ----------------------------------------------------------------------

export const HEADER = {
  MOBILE_HEIGHT: 64,
  MAIN_DESKTOP_HEIGHT: 88,
  DASHBOARD_DESKTOP_HEIGHT: 92,
  DASHBOARD_DESKTOP_OFFSET_HEIGHT: 92 - 32,
};

export const NAVBAR = {
  BASE_WIDTH: 260,
  DASHBOARD_WIDTH: 280,
  DASHBOARD_COLLAPSE_WIDTH: 88,
  //
  DASHBOARD_ITEM_ROOT_HEIGHT: 48,
  DASHBOARD_ITEM_SUB_HEIGHT: 40,
  DASHBOARD_ITEM_HORIZONTAL_HEIGHT: 32,
};

export const ICON = {
  NAVBAR_ITEM: 22,
  NAVBAR_ITEM_HORIZONTAL: 20,
};

// SETTINGS
// Please remove `localStorage` when you set settings.
// ----------------------------------------------------------------------

export const defaultSettings: SettingsValueProps = {
  themeMode: 'light',
  themeDirection: 'ltr',
  themeColorPresets: 'default',
  themeLayout: 'horizontal',
  themeStretch: false,
};
